import React, { useEffect, useState } from 'react';
import { useGoogleAuth, isTokenValid } from '@utility/google_utils';
import { checkIndexedDBForData, storeJSONData, resetIndexedDB, deleteIndexedDbTable } from '@utility/indexedDB_utils';
import LoadingScreen from '@components/LoadingScreen';
import { useNavigate } from 'react-router-dom';
import logo from '@assets/vectors/logo.svg';
import googlelogo from '@assets/images/google-logo.png';
import incoglogo from '@assets/vectors/incognito.svg';
import '@styles/signin.css';
import ParallaxBackground from '@components/ParallaxBackground';
import { importMergeData } from '@utility/import_utils';
import { uploadFile, doesFileExists, downloadFile} from '@utility/google_utils';

const SignIn = () => {
  const { login, reauthenticate, isLoading: isAuthLoading, error: authError } = useGoogleAuth();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isInitializingData, setIsInitializingData] = useState(false);
  const navigate = useNavigate();
  const fileName = "freeweight_database.json";
  const initialJsonPath = "/freeweight_database.json";
  const [showGuestOptions, setShowGuestOptions] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [error, setError] = useState(null);

  const readAndParseFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          resolve(jsonData);
        } catch (error) {
          reject(new Error('Invalid JSON file'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      reader.readAsText(file);
    });
  };

  // Function to fetch and initialize default data
  const initializeUserData = async (isGuest = false) => {
    setIsInitializingData(true);
    try {
      if (isGuest) {
        localStorage.setItem('isGuest', 'true');
        localStorage.setItem('username', 'Guest User');
        localStorage.setItem('profilePicture', '');
      }

      // Check if IndexedDB has data (using the exercises store as indicator)
      const hasData = await checkIndexedDBForData();
      
      if (!hasData) {
        try {
          const response = await fetch('/freeweight_database.json');
          if (!response.ok) throw new Error('Failed to load default data');
          await storeJSONData(await response.json());
        } catch (error) {
          console.error('Using fallback data:', error);
          await resetIndexedDB({
            tables: [
              {
                name: "exercises",
                columns: ["id", "name", "description"],
                rows: []
              },
              // Add other essential tables
            ]
          });
        }
      }
      
      navigate('/home');
    } catch (error) {
      console.error('Initialization failed:', error);
      // Emergency fallback
      await resetIndexedDB();
      navigate('/home');
    } finally {
      setIsInitializingData(false);
    }
  };

  const handleImportData = async () => {
    if (!importFile) {
      setError('❌ Please select a file to import');
      return;
    }

    setIsInitializingData(true);
    
    try {
      // First delete all existing data
      await resetIndexedDB();
      
      // Read and parse the JSON file
      const jsonData = await readAndParseFile(importFile);
      
      // Verify the data structure
      if (!jsonData || !jsonData.tables) {
        throw new Error('Invalid data format - missing tables array');
      }
      
      // Store the new data (this will recreate the database)
      await storeJSONData(jsonData);
      
      // Set guest user data
      localStorage.setItem('isGuest', 'true');
      localStorage.setItem('username', 'Guest User');
      localStorage.setItem('profilePicture', '');
      
      navigate('/home');
    } catch (error) {
      console.error("Import failed:", error);
      setError(`❌ Import failed: ${error.message}`);
      // Fallback to default data if import fails
      await initializeUserData(true);
    } finally {
      setIsInitializingData(false);
      setShowGuestOptions(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const isGuest = localStorage.getItem('isGuest') === 'true';
        
        // Clear any guest data if present when checking for Google auth
        if (isGuest) {
          localStorage.removeItem('isGuest');
          await deleteIndexedDbTable(); // From your indexedDB_utils
        }

        if (accessToken && await isTokenValid(accessToken)) {
          // For Google users, use existing functions to load data
          try {
            // Check if file exists in Drive
            const fileExists = await doesFileExists(accessToken, fileName);
            
            if (fileExists) {
              // Download and store the data
              const jsonData = await downloadFile(accessToken, fileName);
              await storeJSONData(jsonData);
            } else {
              // If no file exists, upload the default data
              await uploadFile(accessToken, fileName, initialJsonPath);
              const jsonData = await downloadFile(accessToken, fileName);
              await storeJSONData(jsonData);
            }
            
            navigate('/home');
          } catch (error) {
            console.error('Failed to load from Drive, using local data:', error);
            navigate('/home');
          }
          return;
        }
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  const handleGuestSignIn = () => {
    setShowGuestOptions(true);
  };

  const handleFreshStart = async () => {
    setShowGuestOptions(false);
    await initializeUserData(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setError(null);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const user = await login();
      if (user) {
        // Check if this is a new user (no existing data)
        if (!localStorage.getItem(fileName)) {
          await initializeUserData();
        } else {
          navigate('/home');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  return (
    <div className="signin-container" style={{ position: 'relative' }}>
      <div className="page-content">
        <ParallaxBackground />
        <img src={logo} alt="logo" className="logo" />
        <p className="title">FreeWeight</p>

        <button onClick={handleGoogleLogin} className="google-signin-btn">
          <img className="google-icon" src={googlelogo} alt="Google logo"></img>
          Sign in with Google
        </button>

        <div style={{
          display: 'flex', 
          alignItems: 'center', 
          margin: '16px 0'
        }}>
          <div style={{
            flexGrow: 1, 
            height: '1px', 
            backgroundColor: '#e0e0e0'
          }}></div>
          <div style={{
            padding: '0 12px', 
            color: '#757575', 
            fontSize: '14px'
          }}>OR</div>
          <div style={{
            flexGrow: 1, 
            height: '1px', 
            backgroundColor: '#e0e0e0'
          }}></div>
        </div>
        
        <button onClick={handleGuestSignIn} className="google-signin-btn">
          <img className="google-icon" src={incoglogo} alt="incognito logo"></img>
          Continue as Guest
        </button>
        
        {authError && <p className="error-message">{authError}</p>}
      </div>

      {showGuestOptions && (
        <div className="modal-overlay">
          <div className="modal-wrapper">
            <div className="modal-content">
              <div className="guest-options-modal">
                <div className="modal-header-s">
                  <h3>Continue as Guest</h3>
                  <button 
                    onClick={() => {
                      setShowGuestOptions(false);
                      setImportFile(null);
                      setError(null);
                    }} 
                    className="close-btn"
                  >
                    &times;
                  </button>
                </div>

                <p>Would you like to start fresh with default data or import your existing data?</p>

                <input 
                    type="file" 
                    accept=".json" 
                    onChange={handleFileChange}
                    disabled={isInitializingData}
                    className="file-input"
                  />

                <div className='button-box'>
                <button onClick={handleFreshStart} className="cancel-button">
                  Start Fresh
                </button>

                <button 
                    onClick={handleImportData} 
                    className="edit-button"
                    disabled={!importFile || isInitializingData}
                  >
                    {isInitializingData ? 'Importing...' : 'Import Data'}
                  </button>
                </div>
                
                {error && <p className="error-message">{error}</p>}
              </div>
            </div>
          </div>
        </div>
      )}
      {(isCheckingAuth || isAuthLoading || isInitializingData) && (
        <LoadingScreen 
          text={
            isCheckingAuth ? "Checking session..." : 
            isInitializingData ? "Initializing your data..." : 
            "Signing in..."
          }
          opacity={0.8}
        />
      )}
    </div>
  );
};

export default SignIn;